import type { AxiosInstance, AxiosResponse } from 'axios';

import { transformObjectToCamelCase, transformObjectToSnakeCase } from '@utilities';

import instantSurveyApi from './api';

const axios = instantSurveyApi as AxiosInstance;

interface PreviewSessionPayload {
  id: string;
}

interface PreviewUrlPerQuotaGroupPayload {
  quotaGroupId?: string | number;
  quotaGroupName?: string;
  previewUrl?: string;
}

interface CreatePreviewUrlPayload {
  preview_url?: string;
  quota_group_id?: string | number;
  quota_group_name?: string;
}

/**
 * Creates a preview session for an instant survey based on the provided selection criteria.
 * Optionally starts at a specific question
 */
export const createPreviewSession = async ({
  instantSurveyId,
  previewSelection,
  signal,
}: {
  instantSurveyId: number;
  previewSelection: {
    quotaGroupId: number;
    startingQuestionId: number;
  };
  signal?: AbortSignal;
}): Promise<PreviewSessionPayload> => {
  return axios({
    data: transformObjectToSnakeCase(previewSelection),
    method: 'post',
    signal,
    url: `/instantsurvey/instantsurvey/${instantSurveyId}/preview_session`,
  }).then(({ data }: AxiosResponse<PreviewSessionPayload>) => data);
};

/**
 * Creates a list of preview urls for every quota group id in a survey.
 * A preview url will be always be formatted as: '?surveyName=name&quotaGroupName=groupName&token=token'
 */
export const createPreviewUrls = async ({
  instantSurveyId,
  signal,
}: {
  instantSurveyId: string | number;
  signal?: AbortSignal;
}): Promise<PreviewUrlPerQuotaGroupPayload[]> => {
  return axios({
    method: 'post',
    signal,
    url: `/instantsurvey/instantsurvey/${instantSurveyId}/shareable_preview_links`,
  }).then(({ data }: AxiosResponse<CreatePreviewUrlPayload[]>) => {
    return transformObjectToCamelCase(data) as PreviewUrlPerQuotaGroupPayload[];
  });
};
